import { useEffect, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { fetchSubmissionByUuid } from "@/api/externalSubmission";
import { FormShell } from "@/components/forms/FormShell";
import { SubmissionAnswersList } from "@/components/forms/SubmissionAnswersList";
import { useCities } from "@/contexts/CitiesProvider";
import { useFormQuery } from "@/hooks/useFormQuery";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useAsyncResource } from "@/hooks/useAsyncResource";
import { formatAnswerTimestamp } from "@/lib/dates/formatAnswerDate";
import {
  EXTERNAL_SUBMISSION_TOKEN_QUERY,
  EXTERNAL_SUBMISSION_UNAVAILABLE_MESSAGE,
  LEGACY_EXTERNAL_SUBMISSION_TOKEN_QUERY,
} from "@/lib/forms/externalSubmission";
import { buildExternalSubmissionView } from "@/lib/forms/externalSubmissionView";
import { collectAllFields } from "@/lib/forms/visibility";

export default function ExternalSubmissionPage() {
  const { uuid: rawUuid } = useParams<{ uuid: string }>();
  const [searchParams] = useSearchParams();
  const uuid = rawUuid?.trim() ?? "";
  const token =
    searchParams.get(EXTERNAL_SUBMISSION_TOKEN_QUERY)?.trim() ||
    searchParams.get(LEGACY_EXTERNAL_SUBMISSION_TOKEN_QUERY)?.trim() ||
    "";
  const canFetch = Boolean(uuid && token);

  const { cities } = useCities();
  const cityNames = useMemo(() => {
    const map: Record<string, string> = {};
    for (const city of cities) {
      map[String(city.id)] = city.name;
    }
    return map;
  }, [cities]);

  const {
    data: submission,
    isLoading: submissionLoading,
    error: submissionError,
  } = useAsyncResource({
    enabled: canFetch,
    deps: [uuid, token],
    fetch: () => fetchSubmissionByUuid(uuid, token),
    formatError: () => EXTERNAL_SUBMISSION_UNAVAILABLE_MESSAGE,
  });

  const formId = submission?.formId ?? submission?.form_id ?? undefined;
  const { data: form, isLoading: formLoading } = useFormQuery(formId);

  const fields = useMemo(
    () => collectAllFields(form?.sections ?? [], form?.fields ?? []),
    [form],
  );

  const rows = useMemo(
    () =>
      submission
        ? buildExternalSubmissionView(submission, fields, cityNames)
        : [],
    [submission, fields, cityNames],
  );

  const createdAt = submission?.createdAt ?? submission?.created_at;
  const error = canFetch
    ? submissionError
    : EXTERNAL_SUBMISSION_UNAVAILABLE_MESSAGE;
  const loading = canFetch && (submissionLoading || (Boolean(formId) && formLoading && !form));

  usePageMeta({
    title: form?.name?.trim() || "Ответ",
  });

  useEffect(() => {
    let robots = document.head.querySelector<HTMLMetaElement>(
      'meta[name="robots"]',
    );
    if (!robots) {
      robots = document.createElement("meta");
      robots.setAttribute("name", "robots");
      document.head.appendChild(robots);
    }
    robots.setAttribute("content", "noindex, nofollow");
    return () => {
      document.head.querySelector('meta[name="robots"]')?.remove();
    };
  }, []);

  return (
    <div className="mx-auto w-full px-4 py-5 sm:px-6 sm:py-8">
      <FormShell appearance={form?.appearance ?? null} className="mx-auto">
        <article className="form-page-card overflow-hidden rounded-2xl border border-slate-200/80 shadow-card-lg">
          {loading ? (
            <div className="animate-pulse space-y-4 px-5 py-8 sm:px-8">
              <div className="h-7 w-2/3 max-w-sm rounded-lg bg-slate-200" />
              <div className="h-4 w-40 rounded bg-slate-100" />
              <div className="mt-6 space-y-3">
                <div className="h-16 rounded-xl bg-slate-100" />
                <div className="h-16 rounded-xl bg-slate-100" />
              </div>
            </div>
          ) : error || !submission ? (
            <div
              className="px-5 py-12 text-center sm:px-8"
              role="alert"
            >
              <h1 className="font-unbounded text-xl font-bold text-ink">
                {EXTERNAL_SUBMISSION_UNAVAILABLE_MESSAGE}
              </h1>
            </div>
          ) : (
            <>
              <header className="border-b border-slate-100 px-5 py-5 sm:px-8 sm:py-6">
                <h1 className="font-unbounded text-xl font-extrabold text-ink sm:text-2xl">
                  {form?.name?.trim() || "Ответ"}
                </h1>
                <p className="mt-1 font-onest text-sm text-slate-500">
                  {formatAnswerTimestamp(createdAt)}
                  {submission.uuid ? (
                    <>
                      {" · "}
                      <code className="font-mono text-xs text-slate-400">
                        {submission.uuid}
                      </code>
                    </>
                  ) : null}
                </p>
              </header>
              <SubmissionAnswersList rows={rows} />
            </>
          )}
        </article>
      </FormShell>
    </div>
  );
}
